import { useMemo } from 'react';
import { PaymentMethod } from '@/types';
import { convertCurrency, getExchangeRate, useRates } from '@/models/rates';
import { SelectedService } from '@/models/manual-invoice.store';
import { TInvoicingCompany } from '@/models/renewals';
import { useOffRampingFeeRate } from '@/models/settings';

export interface CurrencySubtotal {
  currency: string;
  amount: number;
  convertedAmount: number;
  conversionRate: number;
}

export interface InvoiceTotals {
  currencySubtotals: CurrencySubtotal[];
  subtotal: number;
  vatRate: number;
  vatAmount: number;
  total: number;
  dueAmount: number;
  dueCurrency: string;
  offRampingFee?: number;
  offRampingFeeRate?: number;
  exchangeRate?: number;
}

interface UseInvoiceCalculationParams {
  services: SelectedService[];
  invoiceCurrency: 'EUR' | 'USD';
  paymentMethod: PaymentMethod;
  invoicingCompany: TInvoicingCompany | null;
  recipientCountry: string;
}

/**
 * Determines VAT rate based on invoicing company and recipient location
 * Simplified rules:
 * - If same country as invoicing company: apply company's VAT rate
 * - If different country (cross-border): 0% VAT (export)
 */
const calculateVatRate = (
  invoicingCompany: TInvoicingCompany | null,
  recipientCountry: string,
): { rate: number; reason: string } => {
  if (!invoicingCompany) {
    return { rate: 0, reason: 'No invoicing company selected' };
  }

  if (!recipientCountry) {
    return { rate: 0, reason: 'Recipient country not specified' };
  }

  const sellerCountry = invoicingCompany.countryCode;

  if (recipientCountry === sellerCountry) {
    return {
      rate: invoicingCompany.vatRate,
      reason: `Domestic sale (${sellerCountry})`,
    };
  }

  return {
    rate: 0,
    reason: `Cross-border service (${recipientCountry} recipient, ${sellerCountry} company)`,
  };
};

export const useInvoiceCalculation = ({
  services,
  invoiceCurrency,
  paymentMethod,
  invoicingCompany,
  recipientCountry,
}: UseInvoiceCalculationParams): InvoiceTotals => {
  const { data: rates } = useRates();
  const { data: offRampingFeeRate = 0.03 } = useOffRampingFeeRate();

  return useMemo(() => {
    // Group services by currency and calculate subtotals
    const subtotalsByCurrency: Record<string, number> = {};

    for (const service of services) {
      const amount = service.price * service.quantity;
      subtotalsByCurrency[service.currency] =
        (subtotalsByCurrency[service.currency] || 0) + amount;
    }

    // Convert each currency subtotal to invoice currency
    const currencySubtotals: CurrencySubtotal[] = Object.entries(
      subtotalsByCurrency,
    ).map(([currency, amount]) => {
      if (currency === invoiceCurrency) {
        return {
          currency,
          amount,
          convertedAmount: amount,
          conversionRate: 1,
        };
      }

      const rate = getExchangeRate(currency, invoiceCurrency, rates);
      const convertedAmount = convertCurrency(
        amount,
        currency,
        invoiceCurrency,
        rates,
      );

      return {
        currency,
        amount,
        convertedAmount: isNaN(convertedAmount) ? amount : convertedAmount,
        conversionRate: isNaN(rate) ? 1 : rate,
      };
    });

    // Calculate subtotal in invoice currency
    const subtotal = currencySubtotals.reduce(
      (sum, cs) => sum + cs.convertedAmount,
      0,
    );

    // Calculate VAT
    const { rate: vatRate } = calculateVatRate(
      invoicingCompany,
      recipientCountry,
    );
    const vatAmount = subtotal * vatRate;

    // Total in invoice currency
    const total = subtotal + vatAmount;

    // If cryptocurrency, convert to USD and add off-ramping fee
    if (paymentMethod === PaymentMethod.Cryptocurrency) {
      // For crypto, final payment is always in USD
      const usdTotal =
        invoiceCurrency === 'USD'
          ? total
          : convertCurrency(total, invoiceCurrency, 'USD', rates);

      const offRampingFee = usdTotal * offRampingFeeRate;
      const dueAmount = usdTotal + offRampingFee;
      const exchangeRate =
        invoiceCurrency === 'USD'
          ? 1
          : getExchangeRate(invoiceCurrency, 'USD', rates);

      return {
        currencySubtotals,
        subtotal,
        vatRate,
        vatAmount,
        total,
        dueAmount: isNaN(dueAmount) ? total : dueAmount,
        dueCurrency: 'USD',
        offRampingFee: isNaN(offRampingFee) ? 0 : offRampingFee,
        offRampingFeeRate,
        exchangeRate: isNaN(exchangeRate) ? 1 : exchangeRate,
      };
    }

    return {
      currencySubtotals,
      subtotal,
      vatRate,
      vatAmount,
      total,
      dueAmount: total,
      dueCurrency: invoiceCurrency,
    };
  }, [
    services,
    invoiceCurrency,
    paymentMethod,
    invoicingCompany,
    recipientCountry,
    rates,
    offRampingFeeRate,
  ]);
};

export const getVatExplanation = (
  invoicingCompany: TInvoicingCompany | null,
  recipientCountry: string,
): string => {
  const { reason } = calculateVatRate(invoicingCompany, recipientCountry);
  return reason;
};
